// ============================================================================
// OPTIMAL INTEGRITY ENGINE
// Bidirectional API bridge: OptimalAvailability ↔ integrity.optimalworld.org
// Push corrosion/degradation failure modes for RBI assessment
// Pull back inspection results, corrosion rates, FFS outcomes
// ============================================================================

// ---------------------------------------------------------------------------
// INTEGRITY FAILURE MODE CLASSIFIER
// Identifies which failure modes are integrity-related (corrosion, degradation, etc.)
// ---------------------------------------------------------------------------
const INTEGRITY_KEYWORDS = [
  'corrosion', 'erosion', 'pitting', 'cracking', 'fatigue',
  'creep', 'embrittlement', 'degradation', 'thinning', 'cavitation',
  'stress corrosion', 'hydrogen', 'sulfide', 'oxidation', 'scaling',
  'galvanic', 'crevice', 'microbiologically', 'mic', 'cop', 'cui',
  'corrosion under insulation', 'high temperature', 'weld',
  'metallurgical', 'material failure', 'wall loss', 'leak',
  'pressure boundary', 'thickness', 'fitness for service', 'ffs',
  'inspection', 'ndt', 'integrity'
];

function isIntegrityRelated(failureMode) {
  const text = [
    failureMode.description || '',
    failureMode.mechanism || '',
    failureMode.cause || '',
    failureMode.consequence || '',
    failureMode.component || ''
  ].join(' ').toLowerCase();

  return INTEGRITY_KEYWORDS.some(kw => text.includes(kw));
}

function classifyIntegrityModes(failureModes) {
  return failureModes.map(fm => ({
    ...fm,
    isIntegrityRelated: isIntegrityRelated(fm),
    integrityCategory: categorizeIntegrityMode(fm)
  }));
}

function categorizeIntegrityMode(fm) {
  const text = [fm.description || '', fm.mechanism || '', fm.cause || ''].join(' ').toLowerCase();

  if (text.includes('corrosion under insulation') || text.includes('cui')) return 'CUI';
  if (text.includes('stress corrosion') || text.includes('scc')) return 'SCC';
  if (text.includes('hydrogen') || text.includes('hic') || text.includes('sohic')) return 'Hydrogen Damage';
  if (text.includes('fatigue') || text.includes('crack')) return 'Fatigue/Cracking';
  if (text.includes('creep')) return 'Creep';
  if (text.includes('erosion')) return 'Erosion';
  if (text.includes('pitting')) return 'Pitting';
  if (text.includes('galvanic')) return 'Galvanic Corrosion';
  if (text.includes('mic') || text.includes('microbiologically')) return 'MIC';
  if (text.includes('thinning') || text.includes('wall loss') || text.includes('general corrosion')) return 'General Thinning';
  if (text.includes('embrittlement')) return 'Embrittlement';
  if (text.includes('oxidation') || text.includes('scaling')) return 'High Temp Oxidation';
  if (text.includes('corrosion')) return 'General Corrosion';
  if (text.includes('degradation')) return 'Material Degradation';
  return 'Other Integrity';
}

// ---------------------------------------------------------------------------
// API RP 581 DAMAGE MECHANISM MAPPING
// Maps failure mode categories to API 581 damage mechanism IDs
// ---------------------------------------------------------------------------
const DAMAGE_MECHANISM_MAP = {
  'General Thinning': { api581Id: 'DM-1', name: 'Thinning — General', inspectionMethod: 'UT Thickness', defaultRate: 0.25 },
  'General Corrosion': { api581Id: 'DM-1', name: 'Thinning — General', inspectionMethod: 'UT Thickness', defaultRate: 0.15 },
  'Pitting': { api581Id: 'DM-2', name: 'Thinning — Localized (Pitting)', inspectionMethod: 'UT Scanning', defaultRate: 0.5 },
  'CUI': { api581Id: 'DM-3', name: 'CUI — Corrosion Under Insulation', inspectionMethod: 'CUI Inspection / Radiography', defaultRate: 0.3 },
  'SCC': { api581Id: 'DM-4', name: 'SCC — Stress Corrosion Cracking', inspectionMethod: 'WFMT / PAUT', defaultRate: null },
  'Hydrogen Damage': { api581Id: 'DM-5', name: 'Hydrogen Damage (HIC/SOHIC)', inspectionMethod: 'UT Shear Wave', defaultRate: null },
  'Fatigue/Cracking': { api581Id: 'DM-6', name: 'Mechanical Fatigue', inspectionMethod: 'MPI / PAUT', defaultRate: null },
  'Creep': { api581Id: 'DM-7', name: 'High Temperature Creep', inspectionMethod: 'Replication / Hardness', defaultRate: null },
  'Erosion': { api581Id: 'DM-8', name: 'Erosion / Erosion-Corrosion', inspectionMethod: 'UT Thickness / Visual', defaultRate: 0.5 },
  'Galvanic Corrosion': { api581Id: 'DM-9', name: 'Galvanic Corrosion', inspectionMethod: 'Visual / UT', defaultRate: 0.2 },
  'MIC': { api581Id: 'DM-10', name: 'Microbiologically Influenced Corrosion', inspectionMethod: 'Biofilm sampling / UT', defaultRate: 0.3 },
  'Embrittlement': { api581Id: 'DM-11', name: 'Temper Embrittlement / 885°F', inspectionMethod: 'Hardness / Impact testing', defaultRate: null },
  'High Temp Oxidation': { api581Id: 'DM-12', name: 'High Temperature Oxidation', inspectionMethod: 'UT Thickness / Visual', defaultRate: 0.2 },
  'Material Degradation': { api581Id: 'DM-13', name: 'Material Degradation — General', inspectionMethod: 'UT / Visual', defaultRate: 0.1 },
  'Other Integrity': { api581Id: 'DM-99', name: 'Other Integrity Mechanism', inspectionMethod: 'As per RBI', defaultRate: null }
};

// ---------------------------------------------------------------------------
// OPTIMAL INTEGRITY API CLASS
// Full API bridge with auth, asset sync, RBI push/pull, corrosion data
// ---------------------------------------------------------------------------
class OptimalIntegrityAPI {
  constructor(baseUrl = 'https://integrity.optimalworld.org/api/v1') {
    this.baseUrl = baseUrl;
    this.token = null;
    this.tokenExpiry = null;
  }

  // HMAC-SHA256 authentication (same pattern as OptimalSparesAPI)
  async authenticate(email, password) {
    try {
      const response = await fetch(`${this.baseUrl}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      if (!response.ok) return false;
      const data = await response.json();
      this.token = data.token;
      this.tokenExpiry = Date.now() + (24 * 60 * 60 * 1000);
      return true;
    } catch (err) {
      console.warn('IntegrityAPI auth failed:', err);
      return false;
    }
  }

  _headers() {
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${this.token}`
    };
  }

  _isAuthenticated() {
    return this.token && this.tokenExpiry && Date.now() < this.tokenExpiry;
  }

  // Push equipment + failure modes to integrity for RBI assessment
  async pushForRBI(equipment, failureModes) {
    if (!this._isAuthenticated()) throw new Error('Not authenticated');

    const integrityFMs = failureModes.filter(isIntegrityRelated);
    const payload = {
      asset: {
        externalId: equipment.id,
        name: equipment.description || equipment.name || equipment.id,
        type: equipment.type || 'Pressure Vessel',
        material: equipment.material || '',
        designPressure: parseFloat(equipment.designPressure) || null,
        designTemperature: parseFloat(equipment.designTemperature) || null,
        nominalThickness: parseFloat(equipment.nominalThickness) || null,
        installDate: equipment.installDate || null,
        source: 'OptimalAvailability'
      },
      damageMechanisms: integrityFMs.map(fm => {
        const cat = categorizeIntegrityMode(fm);
        const dm = DAMAGE_MECHANISM_MAP[cat] || DAMAGE_MECHANISM_MAP['Other Integrity'];
        return {
          externalFmId: fm.id,
          description: fm.description,
          mechanism: fm.mechanism,
          category: cat,
          api581Id: dm.api581Id,
          inspectionMethod: dm.inspectionMethod,
          estimatedCorrosionRate: dm.defaultRate,
          severity: parseInt(fm.severity) || 5
        };
      })
    };

    const response = await fetch(`${this.baseUrl}/rbi/assess`, {
      method: 'POST',
      headers: this._headers(),
      body: JSON.stringify(payload)
    });
    return response.json();
  }

  // Pull inspection results and corrosion rates for an asset
  async pullInspectionResults(assetExternalId) {
    if (!this._isAuthenticated()) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/assets/${assetExternalId}/inspections`, {
      headers: this._headers()
    });
    if (!response.ok) return null;
    return response.json();
  }

  // Pull corrosion rates for an asset
  async pullCorrosionRates(assetExternalId) {
    if (!this._isAuthenticated()) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/assets/${assetExternalId}/corrosion-rates`, {
      headers: this._headers()
    });
    if (!response.ok) return null;
    return response.json();
  }

  // Pull FFS (Fitness for Service) results for an asset
  async pullFFSResults(assetExternalId) {
    if (!this._isAuthenticated()) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/assets/${assetExternalId}/ffs`, {
      headers: this._headers()
    });
    if (!response.ok) return null;
    return response.json();
  }

  // Pull RBI risk matrix position for an asset
  async pullRiskAssessment(assetExternalId) {
    if (!this._isAuthenticated()) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/rbi/risk/${assetExternalId}`, {
      headers: this._headers()
    });
    if (!response.ok) return null;
    return response.json();
  }

  // Get all assets in integrity system
  async listAssets(page = 1, limit = 50) {
    if (!this._isAuthenticated()) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/assets?page=${page}&limit=${limit}`, {
      headers: this._headers()
    });
    if (!response.ok) return [];
    return response.json();
  }

  // Sync inspection plan back to OptimalAvailability as scheduled tasks
  async pullInspectionPlan(assetExternalId) {
    if (!this._isAuthenticated()) throw new Error('Not authenticated');

    const response = await fetch(`${this.baseUrl}/assets/${assetExternalId}/inspection-plan`, {
      headers: this._headers()
    });
    if (!response.ok) return null;
    return response.json();
  }
}

// ---------------------------------------------------------------------------
// ENRICH FAILURE MODES WITH INTEGRITY DATA
// Adds damage mechanism info, inspection methods, and corrosion rates
// ---------------------------------------------------------------------------
function enrichWithIntegrityData(failureModes) {
  return failureModes.map(fm => {
    if (!isIntegrityRelated(fm)) return fm;

    const cat = categorizeIntegrityMode(fm);
    const dm = DAMAGE_MECHANISM_MAP[cat] || DAMAGE_MECHANISM_MAP['Other Integrity'];

    return {
      ...fm,
      integrityData: {
        isIntegrityRelated: true,
        category: cat,
        api581DamageId: dm.api581Id,
        api581DamageName: dm.name,
        recommendedInspection: dm.inspectionMethod,
        estimatedCorrosionRate: dm.defaultRate,
        requiresRBI: true,
        integrityAppLink: `https://integrity.optimalworld.org/asset/${fm.equipmentId}`
      }
    };
  });
}

// ---------------------------------------------------------------------------
// EXPORTS
// ---------------------------------------------------------------------------
export {
  OptimalIntegrityAPI,
  isIntegrityRelated,
  classifyIntegrityModes,
  categorizeIntegrityMode,
  enrichWithIntegrityData,
  DAMAGE_MECHANISM_MAP,
  INTEGRITY_KEYWORDS
};
